/**
 * WalletIntelligenceScreen — full Wallet Intelligence dashboard.
 * Includes top nav, Investigation Summary, stats, transactions, risk,
 * behavioral analysis, and the Wallet Network graph.
 */
import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightLeft,
  BarChart3,
  Brain,
  Check,
  Copy,
  Link2,
  Search,
  ShieldAlert,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Wallet,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import NetworkBackground from '@/components/clusterscope/NetworkBackground';
import ClusterTopNav from '@/components/clusterscope/ClusterTopNav';
import WalletNetworkGraph from '@/components/clusterscope/WalletNetworkGraph';
import { getWalletIntelligence, type Dataset } from '@/data/clusterscope-data';
import type { WalletIntelligenceData } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

const WALLET_LOADING_STEPS = [
  'Identifying wallet activity...',
  'Building wallet profile...',
  'Detecting connected wallets...',
  'Mapping funding relationships...',
  'Calculating wallet performance...',
  'Finalizing analysis...',
];

interface WalletIntelligenceScreenProps {
  dataset: Dataset;
  initialAddress: string;
  onSwitchMode: (mode: 'token' | 'wallet') => void;
  onWalletScanned: (address: string) => void;
  onNewSearch: () => void;
}

export default function WalletIntelligenceScreen({
  dataset,
  initialAddress,
  onSwitchMode,
  onWalletScanned,
  onNewSearch,
}: WalletIntelligenceScreenProps) {
  const [walletValue, setWalletValue] = useState(initialAddress || '');
  const [scanned, setScanned] = useState(!!initialAddress);
  const [data, setData] = useState<WalletIntelligenceData | null>(
    initialAddress ? getWalletIntelligence(dataset, initialAddress) : null,
  );
  const [selectedNetworkAddress, setSelectedNetworkAddress] = useState(initialAddress || '');
  const [searchValue, setSearchValue] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);

  const handleScan = () => {
    if (!walletValue.trim()) return;
    runAnalysisWithLoading(walletValue.trim());
  };

  const runAnalysisWithLoading = async (address: string) => {
    if (!address.trim()) return;
    setIsAnalyzing(true);
    setAnalysisStep(0);

    for (let i = 0; i < WALLET_LOADING_STEPS.length; i++) {
      setAnalysisStep(i);
      await new Promise((r) => setTimeout(r, 650));
    }

    const intel = getWalletIntelligence(dataset, address.trim());
    setData(intel);
    setSelectedNetworkAddress(address.trim());
    setWalletValue(address.trim());
    setScanned(true);
    onWalletScanned(address.trim());
    setIsAnalyzing(false);
    setSearchValue('');
  };

  const handleSearchSubmit = () => {
    if (searchValue.trim()) {
      runAnalysisWithLoading(searchValue.trim());
    }
  };

  const handleSelectNetworkWallet = useCallback(
    (address: string) => {
      setSelectedNetworkAddress(address);
      const intel = getWalletIntelligence(dataset, address);
      setData(intel);
    },
    [dataset],
  );

  const copyAddress = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    toast.success('Address copied');
  };

  const displayAddress = selectedNetworkAddress || data?.address || '';
  const displayData = data;

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-background">
      <ClusterTopNav
        dataset={dataset}
        mode="wallet"
        onModeChange={onSwitchMode}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearchSubmit={handleSearchSubmit}
        onNewSearch={onNewSearch}
        isAnalyzing={isAnalyzing}
      />

      <div className="relative flex-1 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.25]" />
        <NetworkBackground density={30} />
        <div className="pointer-events-none absolute left-1/4 top-0 h-[440px] w-[440px] rounded-full bg-[#14e0b0] opacity-[0.1] blur-[130px]" />
        <div className="pointer-events-none absolute right-1/4 bottom-0 h-[440px] w-[440px] rounded-full bg-[#a78bfa] opacity-[0.1] blur-[130px]" />

        <div className="relative z-10 flex h-full flex-col overflow-y-auto px-4 py-8 sm:px-8">
          <AnimatePresence>
            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-xl"
              >
                <div className="relative mb-10 h-16 w-16">
                  <div className="absolute inset-0 rounded-full border-[3px] border-white/[0.08]" />
                  <motion.div
                    className="absolute inset-0 rounded-full border-[3px] border-transparent"
                    style={{
                      borderTopColor: 'hsl(var(--primary))',
                      borderRightColor: 'hsl(var(--primary) / 0.35)',
                    }}
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                  />
                </div>
                <div className="flex flex-col gap-3.5">
                  {WALLET_LOADING_STEPS.map((step, i) => {
                    const done = i < analysisStep;
                    const active = i === analysisStep;
                    return (
                      <div key={step} className="flex items-center gap-3">
                        <span className="relative flex h-4 w-4 items-center justify-center">
                          <motion.span
                            className="absolute inset-0 rounded-full"
                            animate={{
                              backgroundColor: done ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.18)',
                              boxShadow: done
                                ? '0 0 10px 2px hsl(var(--primary) / 0.55)'
                                : '0 0 0px 0px transparent',
                            }}
                            transition={{ duration: 0.35 }}
                          />
                          {done && <Check className="relative h-2.5 w-2.5 text-background" strokeWidth={3} />}
                          {active && !done && (
                            <motion.span
                              className="absolute inset-0 rounded-full border border-primary/50"
                              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
                              transition={{ repeat: Infinity, duration: 1 }}
                            />
                          )}
                        </span>
                        <span
                          className={`text-sm transition-colors duration-300 ${
                            done ? 'text-primary' : active ? 'text-foreground/80' : 'text-muted-foreground/50'
                          }`}
                        >
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!scanned ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: easeOut }}
              className="mx-auto flex w-full max-w-lg flex-col items-center pt-20"
            >
              <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#14e0b0]/10 text-[#14e0b0]">
                <Wallet className="h-8 w-8" />
              </div>
              <h1 className="mb-3 text-center text-2xl font-extrabold text-foreground sm:text-3xl">
                Wallet Intelligence
              </h1>
              <p className="mb-8 max-w-md text-center text-sm text-muted-foreground">
                Enter a Solana wallet address to generate a full profitability breakdown, win rate analysis,
                behavioral fingerprint, and cluster affiliation report.
              </p>

              <div className="flex w-full max-w-md items-stretch gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <input
                    value={walletValue}
                    onChange={(e) => setWalletValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                    placeholder="Solana wallet address..."
                    className="w-full rounded-xl border border-white/10 bg-black/30 py-3 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-[#14e0b0]/50"
                  />
                </div>
                <button
                  onClick={handleScan}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#14e0b0] px-5 py-3 text-sm font-bold text-[#04140f] transition-transform hover:scale-[1.03] active:scale-95"
                >
                  Analyze
                </button>
              </div>

              <div className="mt-4 flex gap-2">
                {dataset.tryWallets.map((w) => (
                  <button
                    key={w}
                    onClick={() => {
                      setWalletValue(w);
                      runAnalysisWithLoading(w);
                    }}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[#14e0b0]/50 hover:text-foreground"
                  >
                    Try {w}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            displayData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: easeOut }}
                className="mx-auto w-full max-w-6xl space-y-8 pb-12"
              >
                {/* Header with address */}
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#14e0b0] shadow-[0_0_8px_2px_rgba(20,224,176,0.5)]" />
                      <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                        Analyzed Wallet
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-lg font-bold text-foreground sm:text-xl">{displayAddress}</span>
                      <button
                        onClick={() => copyAddress(displayAddress)}
                        className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
                      >
                        <Copy className="h-3 w-3" />
                        Copy
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                      <span className="font-bold text-foreground">{displayData.summary.totalTrades.toLocaleString()}</span> trades
                    </span>
                    <span className="rounded-lg border border-[#14e0b0]/30 bg-[#14e0b0]/10 px-3 py-1.5 text-xs font-bold text-[#14e0b0]">
                      {displayData.summary.winRate}% WR
                    </span>
                  </div>
                </div>

                {/* Investigation Summary */}
                <div className="rounded-2xl border border-[#14e0b0]/20 bg-[#14e0b0]/[0.04] p-5">
                  <div className="mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#14e0b0]" />
                    <h3 className="text-sm font-bold text-foreground">Investigation Summary</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {displayData.investigationSummary}
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  <SummaryCard icon={<TrendingUp className="h-4 w-4 text-[#14e0b0]" />} value={displayData.summary.totalPnl} label="Total PnL" />
                  <SummaryCard icon={<BarChart3 className="h-4 w-4 text-[#a78bfa]" />} value={`${displayData.summary.winRate}%`} label="Win Rate" />
                  <SummaryCard icon={<Zap className="h-4 w-4 text-[#fb923c]" />} value={displayData.summary.totalTrades.toLocaleString()} label="Total Trades" />
                  <SummaryCard icon={<ArrowRightLeft className="h-4 w-4 text-[#f472b6]" />} value={displayData.summary.avgHoldTime} label="Avg Hold" />
                  <SummaryCard icon={<Wallet className="h-4 w-4 text-primary" />} value={displayData.summary.totalVolume} label="Total Volume" />
                  <SummaryCard icon={<Link2 className="h-4 w-4 text-muted-foreground" />} value={displayData.funding.source} label="Funding Source" />
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                  {/* Connected Wallets */}
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
                    <div className="mb-4 flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-[#a78bfa]" />
                      <h3 className="text-sm font-bold text-foreground">Connected Wallets</h3>
                    </div>
                    <div className="space-y-2.5">
                      {displayData.connectedWallets.map((w, i) => (
                        <motion.div
                          key={w.address}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * i, ease: easeOut }}
                          className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
                        >
                          <div>
                            <div className="font-mono text-sm font-semibold text-foreground">{w.address}</div>
                            <div className="text-[11px] text-muted-foreground">{w.relationship}</div>
                          </div>
                          <span
                            className="text-[11px] font-bold"
                            style={{ color: w.strength > 80 ? '#14e0b0' : w.strength > 50 ? '#fb923c' : '#a78bfa' }}
                          >
                            {w.strength}%
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
                    <div className="mb-4 flex items-center gap-2">
                      <ArrowRightLeft className="h-4 w-4 text-[#14e0b0]" />
                      <h3 className="text-sm font-bold text-foreground">Recent Transactions</h3>
                    </div>
                    <div className="space-y-2.5">
                      {displayData.recentTransactions.map((tx, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * i, ease: easeOut }}
                          className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
                        >
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold ${tx.type === 'Buy' ? 'bg-[#14e0b0]/15 text-[#14e0b0]' : 'bg-[#fb923c]/15 text-[#fb923c]'}`}
                            >
                              {tx.type === 'Buy' ? 'B' : 'S'}
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-foreground">
                                {tx.type} {tx.token}
                              </div>
                              <div className="text-[11px] text-muted-foreground">{tx.date}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-foreground">{tx.amount}</div>
                            <div className={`text-[11px] font-semibold ${tx.pnl.startsWith('+') ? 'text-[#14e0b0]' : 'text-[#fb923c]'}`}>{tx.pnl}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Risk & Behavioral */}
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
                      <div className="mb-4 flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-[#fb923c]" />
                        <h3 className="text-sm font-bold text-foreground">Risk Indicators</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {displayData.riskIndicators.map((r) => (
                          <span
                            key={r}
                            className="rounded-md border border-[#fb923c]/30 bg-[#fb923c]/10 px-2.5 py-1 text-[11px] font-semibold text-[#fb923c]"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
                      <div className="mb-4 flex items-center gap-2">
                        <Brain className="h-4 w-4 text-[#a78bfa]" />
                        <h3 className="text-sm font-bold text-foreground">Behavioural Analysis</h3>
                      </div>
                      <div className="space-y-3 text-[11px]">
                        <BehaviourRow label="Trading Style" value={displayData.behavioralAnalysis.tradingStyle} />
                        <BehaviourRow label="Time Preference" value={displayData.behavioralAnalysis.timePreference} />
                        <BehaviourRow label="Token Concentration" value={displayData.behavioralAnalysis.tokenConcentration} />
                        <BehaviourRow label="Avg Entry Size" value={displayData.behavioralAnalysis.avgEntrySize} />
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2">
                            <div className="text-muted-foreground">Largest Win</div>
                            <div className="mt-0.5 font-bold text-[#14e0b0]">{displayData.behavioralAnalysis.largestWin}</div>
                          </div>
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2">
                            <div className="text-muted-foreground">Largest Loss</div>
                            <div className="mt-0.5 font-bold text-[#fb923c]">{displayData.behavioralAnalysis.largestLoss}</div>
                          </div>
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2">
                            <div className="text-muted-foreground">Profit Factor</div>
                            <div className="mt-0.5 font-bold text-foreground">{displayData.behavioralAnalysis.profitFactor}</div>
                          </div>
                          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2">
                            <div className="text-muted-foreground">Sharpe Ratio</div>
                            <div className="mt-0.5 font-bold text-foreground">{displayData.behavioralAnalysis.sharpeRatio}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wallet Network Graph */}
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
                  <div className="mb-4 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-bold text-foreground">Wallet Network</h3>
                  </div>
                  <WalletNetworkGraph
                    data={displayData}
                    onSelectWallet={handleSelectNetworkWallet}
                    selectedAddress={selectedNetworkAddress}
                  />
                </div>
              </motion.div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2, ease: easeOut }}
      className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 transition-colors hover:border-white/[0.15]"
    >
      <div className="mb-2">{icon}</div>
      <div className="text-lg font-extrabold text-foreground">{value}</div>
      <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </motion.div>
  );
}

function BehaviourRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground sm:text-right">{value}</span>
    </div>
  );
}
